/**
 * Redactor for Connection Diagnostics telemetry (Slice 4).
 *
 * Strict allow-list: only the fields below ship to the backend. Everything
 * else (UPN, tenant id, server identity strings, db names, error messages)
 * is dropped. Used by both the renderer (for *Copy details*) and the main
 * process (for `analytics:send-diagnostic-report`).
 *
 * See `Ralph/ConnectionDiagnostics/PRD.md` → Slice 4 for the allowlist.
 */
import type {
  DiagnosticsReport,
  DiagnosticsEngine,
  DiagnosticsAuthKind,
  DiagnosticsConnectTransport,
  DiagnosticsListingAdapter,
  DiagnosticsProbeFailureReasons,
  DiagnosticsTrigger,
  DiagnosticsTenantMode,
  ListingErrorReason,
  QueryExecutionSite,
} from '../../../types/connectionDiagnostics';

/** Allowed-fields-only payload that ships to telemetry. */
export type SendableDiagnosticPayload = {
  reportId: string;
  generatedAt: number;
  engine: DiagnosticsEngine;
  authKind: DiagnosticsAuthKind;
  tenantMode: DiagnosticsTenantMode;
  triggeredBy: DiagnosticsTrigger;
  /** Low-cardinality engine-edition slug (Azure SQL DB / Managed Instance / etc.). */
  engineEdition?: string;
  capabilities?: {
    isSysadmin?: boolean;
    canViewAnyDatabase?: boolean;
    canViewServerState?: boolean;
    hasMasterAccess?: boolean;
  };
  databases: {
    listingSucceeded: boolean;
    rawCount: number;
    accessibleCount: number;
    /** Only the SQL error code is allowed — never the error message. */
    listingErrorSqlCode?: number;
    /** Bucketed listing duration for distinguishing instant-fail vs timeout. */
    listingDurationMsBucket?: '<100' | '100-1000' | '1000-5000' | '5000-15000' | '>=15000';
    listingTimedOut?: boolean;
    /**
     * Low-cardinality reason for the listing failure. Fixed enum — safe to
     * ship as-is. Absent when listing succeeded.
     */
    listingErrorReason?: ListingErrorReason;
    /**
     * Item 5 — which engine adapter executed the user's last production
     * listing. Two-value enum, PII-safe.
     */
    listingAdapter?: DiagnosticsListingAdapter;
  };
  /** Pool-open scope error code (no message, no DB name). */
  scopeErrorSqlCode?: number;
  /** Engine name when the SQL battery is not implemented for this engine. */
  notImplementedFor?: DiagnosticsEngine;
  /** HMAC-SHA-256 of MSAL `homeAccountId` (`<oid>.<tid>`) with build-time salt. Absent when not Entra. */
  homeAccountIdHash?: string;
  /**
   * Item 4 — connect-transport for this connection. Top-level (not nested
   * under `connection`) so the one-line summary string can include it
   * without an extra null check. PII-safe — 4-value enum.
   */
  connectTransport?: DiagnosticsConnectTransport;
  /**
   * Item 3 — per-probe failure reasons (Q1/Q2/Q4). Same shape as on the
   * report — passed through by reference because each value is already a
   * fixed-cardinality enum. Absent when no probes failed.
   */
  probeFailureReasons?: DiagnosticsProbeFailureReasons;
  /**
   * Query stall payload — only present when `triggeredBy === 'query_execution_stall'`.
   * All fields are low-cardinality numeric/categorical — no PII, no redaction needed.
   */
  stall?: {
    verdict: 'no_notifications' | 'silent_after_progress' | 'progressing';
    batchCountAttempted: number;
    methodHistogram: Record<string, number>;
    elapsedMs: number;
    /** The configured query timeout that fired, so elapsedMs is interpretable (elapsed ≈ timeout + connect/setup overhead for single-batch executions). */
    timeoutMs?: number;
    /** The hung batch's OWN elapsed time (ms), measured from that batch's dispatch — excludes connect, USE and earlier GO batches. Suppression compares this (not elapsedMs) against timeoutMs when present, so pre-batch overhead can't defeat the overshoot window. */
    batchElapsedMs?: number;
    /** Which surface issued the execution, from the execution id's shape — bounded vocabulary (`explorer_preload` = the Object Explorer's background preload fan-out, the only gated site; `explorer` = a user-initiated Object Explorer fetch — folder expand, Script as CREATE, columns/keys/indexes). Part of the signature key. */
    site?: QueryExecutionSite;
    /** Time (ms) the execution waited for a per-connection SQL Tools gate slot before its login. Outside the batch timeout clock. */
    queueMs?: number;
    /** Time (ms) the per-execution `connection/connect` took before the first batch was dispatched. */
    connectMs?: number;
    /** Method name of the LAST notification received for the hung batch (raw `query/*`, bounded by the SSTS protocol vocabulary). Names where in the pipeline delivery stopped. */
    lastNotifMethod?: string;
    /** Age (ms) of that last notification when the timeout fired — separates "just went dark" from "dark for the whole timeout". */
    lastNotifAgeMs?: number;
    /** ms since the OS last resumed from sleep (absent when no resume this session) — the sleep/resume discriminator. */
    sinceResumeMs?: number;
    /** STS execution gate occupancy for this connection when the timeout fired. */
    gateActive?: number;
    gateQueued?: number;
    /** 1-based count of reports sent this app session for this (engine, verdict, notification-set, site) signature. Capped sender-side so one retried query reads as one incident, not a flood. */
    occurrenceCount?: number;
  };
};

/**
 * Bucket the listing duration into a small set of categorical values. The raw
 * millisecond value would be high-cardinality and reveal request-latency
 * patterns; the bucket lets us tell "instant-fail (permission)" apart from
 * "timed out at 15 s (Hyperscale cold-cache)" which is the diagnostic point.
 */
function bucketDuration(ms: number | undefined): SendableDiagnosticPayload['databases']['listingDurationMsBucket'] {
  if (typeof ms !== 'number') return undefined;
  if (ms < 100) return '<100';
  if (ms < 1000) return '100-1000';
  if (ms < 5000) return '1000-5000';
  if (ms < 15000) return '5000-15000';
  return '>=15000';
}

const DEV_FALLBACK_SALT = 'jam-sql-dev-salt';

/**
 * Resolve the build-time salt for `homeAccountIdHash`.
 * - Renderer: `import.meta.env.VITE_DIAGNOSTIC_HASH_SALT` (injected by Vite).
 * - Main process: `process.env.VITE_DIAGNOSTIC_HASH_SALT` (injected via vite.config.ts `define`).
 * - Fallback: deterministic dev value, so tests are stable without env setup.
 */
export function resolveDiagnosticHashSalt(): string {
  // Renderer path — `import.meta.env` is replaced at build time.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta = (import.meta as any)?.env;
    const fromMeta = meta?.VITE_DIAGNOSTIC_HASH_SALT;
    if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  } catch {
    // import.meta unavailable in some test environments — fall through.
  }
  // Main-process path — `process.env.VITE_DIAGNOSTIC_HASH_SALT` is statically
  // replaced by `vite.config.ts` `define`.
  if (typeof process !== 'undefined') {
    const fromEnv = process.env?.VITE_DIAGNOSTIC_HASH_SALT;
    if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  }
  return DEV_FALLBACK_SALT;
}

/** Compute the MSAL `homeAccountId` value from an Entra report (`<oid>.<tid>`). */
function buildHomeAccountId(report: DiagnosticsReport): string | undefined {
  const oid = report.identity?.objectId;
  const tid = report.identity?.tenantId;
  if (!oid || !tid) return undefined;
  return `${oid}.${tid}`;
}

/** HMAC-SHA-256(salt, message) → lowercase hex. Uses Web Crypto (Node 19+ + browser). */
async function hmacSha256Hex(salt: string, message: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    // No subtle crypto means we can't safely produce a hash — drop the field
    // rather than emit a weak placeholder.
    throw new Error('Web Crypto API not available');
  }
  const encoder = new TextEncoder();
  const key = await subtle.importKey(
    'raw',
    encoder.encode(salt),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await subtle.sign('HMAC', key, encoder.encode(message));
  const bytes = new Uint8Array(sig);
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

/**
 * Strip every disallowed field from `report` and emit the telemetry-safe shape.
 * Computes `homeAccountIdHash` for Entra reports using the build-time salt.
 *
 * Async because Web Crypto's `subtle.sign` is async. Callers that don't need
 * a hash (e.g. unit tests asserting the strip-only behaviour) may pass a
 * non-Entra report and still resolve synchronously-equivalent (no hash).
 */
export async function redactDiagnosticPayload(
  report: DiagnosticsReport,
  options?: { salt?: string },
): Promise<SendableDiagnosticPayload> {
  const payload: SendableDiagnosticPayload = {
    reportId: report.reportId,
    generatedAt: report.generatedAt,
    engine: report.engine,
    authKind: report.authKind,
    tenantMode: report.tenantMode,
    triggeredBy: report.triggeredBy,
    databases: {
      listingSucceeded: report.databases.listingSucceeded,
      rawCount: report.databases.rawCount,
      accessibleCount: report.databases.accessibleCount,
      listingErrorSqlCode: report.databases.listingError?.sqlErrorCode,
      ...(bucketDuration(report.databases.listingDurationMs) !== undefined
        ? { listingDurationMsBucket: bucketDuration(report.databases.listingDurationMs) }
        : {}),
      ...(report.databases.listingTimedOut ? { listingTimedOut: true } : {}),
      ...(report.databases.listingErrorReason
        ? { listingErrorReason: report.databases.listingErrorReason }
        : {}),
      ...(report.databases.listingAdapter
        ? { listingAdapter: report.databases.listingAdapter }
        : {}),
    },
  };

  // Item 4 — surface the connect-transport at the top level. Read out of
  // the nested `connection` shape on the report; absent when the dep
  // didn't return a value (e.g. non-MSSQL).
  if (report.connection?.connectTransport) {
    payload.connectTransport = report.connection.connectTransport;
  }

  // Item 3 — pass `probeFailureReasons` through by reference. Each field
  // is already a fixed-cardinality enum so there's nothing to strip.
  if (report.probeFailureReasons) {
    payload.probeFailureReasons = report.probeFailureReasons;
  }

  if (report.serverEdition?.engineEdition) {
    payload.engineEdition = report.serverEdition.engineEdition;
  }

  if (report.capabilities) {
    payload.capabilities = {
      isSysadmin: report.capabilities.isSysadmin,
      canViewAnyDatabase: report.capabilities.canViewAnyDatabase,
      canViewServerState: report.capabilities.canViewServerState,
      hasMasterAccess: report.capabilities.hasMasterAccess,
    };
  }

  if (typeof report.scopeError?.sqlErrorCode === 'number') {
    payload.scopeErrorSqlCode = report.scopeError.sqlErrorCode;
  }

  if (report.notImplementedFor) {
    payload.notImplementedFor = report.notImplementedFor;
  }

  // Stall payload — all fields are low-cardinality numeric/categorical, no PII.
  // Pass through by reference when present.
  if (report.stall) {
    payload.stall = report.stall;
  }

  const homeAccountId = buildHomeAccountId(report);
  if (homeAccountId) {
    try {
      const salt = options?.salt ?? resolveDiagnosticHashSalt();
      payload.homeAccountIdHash = await hmacSha256Hex(salt, homeAccountId);
    } catch {
      // Hash can't be computed — drop the field rather than ship a weak placeholder.
    }
  }

  return payload;
}

function formatBool(value: boolean | undefined): string {
  if (value === undefined) return 'unknown';
  return value ? 'yes' : 'no';
}

/**
 * Format the redacted payload as a copyable plain-text block. Each line is
 * `key: value`. Used by the *Copy details* button.
 */
export function formatRedactedPayloadForClipboard(
  payload: SendableDiagnosticPayload,
): string {
  const lines: string[] = [];
  lines.push(`reportId: ${payload.reportId}`);
  lines.push(`generatedAt: ${new Date(payload.generatedAt).toISOString()}`);
  lines.push(`triggeredBy: ${payload.triggeredBy}`);
  lines.push(`engine: ${payload.engine}`);
  lines.push(`authKind: ${payload.authKind}`);
  lines.push(`tenantMode: ${payload.tenantMode ?? 'null'}`);
  if (payload.notImplementedFor) {
    lines.push(`notImplementedFor: ${payload.notImplementedFor}`);
  }
  if (payload.engineEdition) {
    lines.push(`engineEdition: ${payload.engineEdition}`);
  }
  lines.push(`isSysadmin: ${formatBool(payload.capabilities?.isSysadmin)}`);
  lines.push(`canViewAnyDatabase: ${formatBool(payload.capabilities?.canViewAnyDatabase)}`);
  lines.push(`canViewServerState: ${formatBool(payload.capabilities?.canViewServerState)}`);
  lines.push(`hasMasterAccess: ${formatBool(payload.capabilities?.hasMasterAccess)}`);
  lines.push(`databases.listingSucceeded: ${payload.databases.listingSucceeded}`);
  lines.push(`databases.rawCount: ${payload.databases.rawCount}`);
  lines.push(`databases.accessibleCount: ${payload.databases.accessibleCount}`);
  if (payload.databases.listingErrorSqlCode !== undefined) {
    lines.push(`databases.listingErrorSqlCode: ${payload.databases.listingErrorSqlCode}`);
  }
  if (payload.databases.listingDurationMsBucket) {
    lines.push(`databases.listingDurationMsBucket: ${payload.databases.listingDurationMsBucket}`);
  }
  if (payload.databases.listingTimedOut) {
    lines.push(`databases.listingTimedOut: true`);
  }
  if (payload.databases.listingErrorReason) {
    lines.push(`databases.listingErrorReason: ${payload.databases.listingErrorReason}`);
  }
  if (payload.databases.listingAdapter) {
    lines.push(`databases.listingAdapter: ${payload.databases.listingAdapter}`);
  }
  if (payload.scopeErrorSqlCode !== undefined) {
    lines.push(`scopeErrorSqlCode: ${payload.scopeErrorSqlCode}`);
  }
  if (payload.connectTransport) {
    lines.push(`connectTransport: ${payload.connectTransport}`);
  }
  if (payload.probeFailureReasons?.identity) {
    lines.push(`probeFailureReasons.identity: ${payload.probeFailureReasons.identity}`);
  }
  if (payload.probeFailureReasons?.capabilities) {
    lines.push(
      `probeFailureReasons.capabilities: ${payload.probeFailureReasons.capabilities}`
    );
  }
  if (payload.probeFailureReasons?.edition) {
    lines.push(`probeFailureReasons.edition: ${payload.probeFailureReasons.edition}`);
  }
  if (payload.homeAccountIdHash) {
    lines.push(`homeAccountIdHash: ${payload.homeAccountIdHash}`);
  }
  return lines.join('\n');
}
